import React from 'react';
import { Skeleton } from '@shared/ui/skeleton';
import { Card, CardContent, CardHeader } from '@shared/ui/card';

interface LoadingSkeletonProps {
  variant?: 'page' | 'card' | 'list' | 'dashboard';
  count?: number;
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = 'page',
  count = 1,
}) => {
  switch (variant) {
    case 'dashboard':
      return (
        <div className='p-4 sm:p-6 lg:p-8 space-y-6'>
          {/* Header skeleton */}
          <div className='space-y-2'>
            <Skeleton className='h-8 w-[250px]' />
            <Skeleton className='h-4 w-[400px]' />
          </div>

          {/* Stats cards skeleton */}
          <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-4'>
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <Skeleton className='h-4 w-[100px]' />
                  <Skeleton className='h-4 w-4' />
                </CardHeader>
                <CardContent>
                  <Skeleton className='h-7 w-[80px] mb-1' />
                  <Skeleton className='h-3 w-[120px]' />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Main content skeleton */}
          <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-7'>
            <Card className='col-span-4'>
              <CardHeader>
                <Skeleton className='h-6 w-[200px]' />
              </CardHeader>
              <CardContent className='space-y-4'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='flex items-center space-x-4'>
                    <Skeleton className='h-12 w-12 rounded-full' />
                    <div className='space-y-2 flex-1'>
                      <Skeleton className='h-4 w-[200px]' />
                      <Skeleton className='h-3 w-[150px]' />
                    </div>
                    <Skeleton className='h-8 w-[80px]' />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className='col-span-3'>
              <CardHeader>
                <Skeleton className='h-6 w-[150px]' />
              </CardHeader>
              <CardContent className='space-y-4'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className='flex items-center justify-between'>
                    <div className='space-y-1'>
                      <Skeleton className='h-4 w-[120px]' />
                      <Skeleton className='h-3 w-[80px]' />
                    </div>
                    <Skeleton className='h-6 w-[60px]' />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      );

    case 'card':
      return (
        <div className='p-4 sm:p-6 space-y-4'>
          {Array.from({ length: count }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className='flex items-center space-x-4'>
                  <Skeleton className='h-12 w-12 rounded-full' />
                  <div className='space-y-2'>
                    <Skeleton className='h-4 w-[200px]' />
                    <Skeleton className='h-3 w-[150px]' />
                  </div>
                </div>
              </CardHeader>
              <CardContent className='space-y-2'>
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-4 w-[80%]' />
                <Skeleton className='h-4 w-[60%]' />
              </CardContent>
            </Card>
          ))}
        </div>
      );

    case 'list':
      return (
        <div className='p-4 sm:p-6 space-y-3'>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className='flex items-center space-x-4 p-4 border rounded-lg'>
              <Skeleton className='h-10 w-10 rounded-full' />
              <div className='space-y-2 flex-1'>
                <Skeleton className='h-4 w-[200px]' />
                <Skeleton className='h-3 w-[150px]' />
              </div>
              <Skeleton className='h-8 w-[80px]' />
            </div>
          ))}
        </div>
      );

    case 'page':
    default:
      return (
        <div className='flex min-h-[400px] items-center justify-center p-4 sm:p-6'>
          <div className='space-y-4 w-full max-w-md'>
            <div className='flex items-center space-x-4'>
              <Skeleton className='h-12 w-12 rounded-full' />
              <div className='space-y-2'>
                <Skeleton className='h-4 w-[200px]' />
                <Skeleton className='h-3 w-[150px]' />
              </div>
            </div>
            <div className='space-y-2'>
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-[80%]' />
              <Skeleton className='h-4 w-[60%]' />
            </div>
          </div>
        </div>
      );
  }
};
